"use client";

import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import Head from "next/head";

import RewardCard from "./partials/reward-card";
import styles from "./rewards.module.scss";
import services from "@/store/services";
import Header from "./partials/header";
import Components from "@/components";
import Icons from "@/assets/icons";
import sk from "@/skeletons";
import api from "@/api";

const Rewards = () => {
  const { redeemModal } = useSelector((state) => state.modal);
  const user = useSelector((state) => state.user.users);
  const dispatch = useDispatch();
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [rewards, setRewards] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const res = await api.prizes();
    if (res.status === 200) setRewards(res.data.result);
    else dispatch(services.failureAlert("Error in fetching rewards!"));

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <Head>
        <title>
          Educational Reward Program | Redeem Your Cipher Rewards |
          CipherSchools
        </title>
        <meta name="author" content="Cipher Schools" />
        <meta
          name="description"
          content="Join Cipher Schools' Educational Reward Program! Redeem points for exciting perks and boost your learning journey with us."
        />
        <meta
          name="keywords"
          content="CipherSchools, online courses, programming courses, learn data science, design courses, top instructors, coding tutorials, tech skills"
        />
        <meta name="robots" content="index, follow" />
        <meta name="image" content={"images/Cipherschools_512x512.png"} />
        <meta
          property="og:title"
          content="Educational Reward Program | Redeem Your Cipher Rewards"
        />
        <meta
          property="og:description"
          content="Join Cipher Schools' Educational Reward Program! Redeem points for exciting perks and boost your learning journey with us."
        />
        <meta
          property="og:url"
          content="https://www.cipherschools.com/redeem/rewards"
        />
        <meta property="og:type" content="website" />
      </Head>
      <main>
        <Header type={""} />
        <section className={styles.container}>
          <article>
            {loading ? (
              <sk.RowCardSkeleton number={4} />
            ) : (
              rewards?.map((reward) => (
                <RewardCard {...reward} key={reward._id} />
              ))
            )}
          </article>
          <hr />
          <div className={styles.bottomSection}>
            <h1 id="policy" className={styles.tittle}>
              Cipherpoints Policy
            </h1>
            <article>
              <h2>What are CipherPoints?</h2>
              <p>
                CipherPoints are credits you earn on the platform. You can
                accumulate these points by completing various daily missions and
                watching videos. Each activity rewards you with a certain number
                of CipherPoints, which you can then use for various benefits or
                rewards within the platform. This system encourages regular
                engagement and motivates users to complete the courses they have
                enrolled in.
              </p>
            </article>
            <article>
              <h2>How can you earn CipherPoints?</h2>
              <p>
                You can earn CipherPoints by investing your time in learning on
                the platform. CipherPoints are distributed based on the time
                spent watching videos.
                <br />
                Below is the breakdown:
                <br />
                <br />
                <table className={styles["cipher-table"]}>
                  <thead>
                    <tr>
                      <th>Learning Time</th>
                      <th>CipherPoints Earned</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>5 minutes</td>
                      <td>5 CipherPoints</td>
                    </tr>
                    <tr>
                      <td>30 minutes</td>
                      <td>15 CipherPoints</td>
                    </tr>
                    <tr>
                      <td>60 minutes</td>
                      <td>30 CipherPoints</td>
                    </tr>
                    <tr>
                      <td>120 minutes</td>
                      <td>50 CipherPoints</td>
                    </tr>
                  </tbody>
                </table>
                <br />
                The maximum CipherPoints one can earn in a single day is capped
                at 100. Additionally, you can earn CipherPoints by completing
                your daily missions. To explore more &nbsp;
                <a
                  onClick={() =>
                    user?.email
                      ? router.push("/rewards/cipherpoints")
                      : dispatch(services.toggleSigninModal())
                  }
                >
                  Click here
                </a>
                .
              </p>
            </article>
            <article>
              <h2>How can you redeem CipherPoints?</h2>
              <p>
                You can redeem CipherPoints by either clicking on the{" "}
                <Image
                  src={Icons.WatchPoints}
                  alt={"Cipherpoints"}
                  width={20}
                  height={20}
                />
                &nbsp; on the top right corner of the navigation bar in desktop
                mode or by clicking on the “My CipherPoint” option available in
                the “Profile” section in mobile view. Additionally, you can also
                redeem your CipherPoints by visiting the “Store” section
                available on the left horizontal navigation bar on the website.
              </p>
            </article>
            <article>
              <h2>What can I redeem CipherPoints for?</h2>
              <p>
                You can redeem CipherPoints for rewards like T-shirts and other
                merchandise in the store section. Once redeemed, you will need
                to complete another course from scratch/start before you can use
                your CipherPoints to redeem your next reward, after 60 days of
                redeeming the previous merchandise.
              </p>
            </article>
          </div>
        </section>
      </main>

      <Components.Modal
        placement="top"
        showModal={redeemModal}
        className={"redeemModal"}
        heading={"Complete your profile"}
        setShowModal={() => dispatch(services.toggleRedeemModal())}
      >
        <Components.RedeemModal />
      </Components.Modal>
    </>
  );
};

export default Components.Wrapper(Rewards, "", "", styles.wrapper);
